import { CalendarCheck, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { clock } from '../lib/format';
import type { Child } from '../lib/types';
import { useParent } from './ParentContext';
import { Link } from 'react-router';
import { SplitCell, SplitEditor, TimeBySubject } from './SubjectHours';
import { ConnexusAttendanceCard, SchoolBanner, useSchoolLink } from './SchoolLink';
import type { SubjectPart } from '../lib/types';

interface AttendanceDay {
  date: string;
  weekday: string;
  schoolDay: boolean;
  actualHours: number | null;
  /** Why it was 0 hours (#298). */
  reason?: string;
  /** Where it came from: 'connexus' (finished lessons, #673), 'connexus-attendance' (Connexus's own, #680), or '' when entered here. */
  source?: string;
  /** Field trips that day (#622). */
  fieldTrips?: string[];
  /** In a closed school year (#623): read-only. */
  locked?: boolean;
  estimatedHours: number;
  /** The estimate was saved at the end of that day (#333). */
  estimateStored?: boolean;
  firstSeen: number | null;
  lastSeen: number | null;
}
interface AttendanceWeek {
  weekStart: string;
  today: string;
  days: AttendanceDay[];
  totals: { actualHours: number; estimatedHours: number };
}

const shiftWeek = (weekStart: string, n: number) => {
  const [y, m, d] = weekStart.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + 7 * n)).toISOString().slice(0, 10);
};
const short = (date: string) => new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const hrs = (h: number) => `${Number(h.toFixed(2))} h`;

/** Why a school day was 0 hours (#298). */
const REASONS = [
  { id: 'excused', label: 'Excused absence' },
  { id: 'unexcused', label: 'Unexcused absence' },
  { id: 'vacation', label: 'Vacation day' },
  { id: 'holiday', label: 'Holiday' },
];

/**
 * Attendance (#166): the hours a parent records for each day, next to the system's estimate (monitored
 * active time), a week at a time with previous/next weeks.
 */
export function AttendanceTab({ child }: { child: Child }) {
  const { toast, refresh } = useParent();
  const [week, setWeek] = useState<string | null>(null); // any date in the week; null = this week
  const [data, setData] = useState<AttendanceWeek | null>(null);
  // Hours by subject (#607): this week's splits, the day being split, and a counter to refresh the year's totals.
  const [splits, setSplits] = useState<Record<string, SubjectPart[]>>({});
  const [splitting, setSplitting] = useState('');
  const [splitVersion, setSplitVersion] = useState(0);

  // Connexus attendance (#662), read on Update now.
  const { info: school, setInfo: setSchool } = useSchoolLink(child.id);
  const load = useCallback(() => {
    api
      .get<AttendanceWeek>(`/api/children/${child.id}/attendance/week${week ? `?week=${week}` : ''}`)
      .then(setData)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [child.id, week, toast]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!data) return;
    api
      .get<{ days: Record<string, SubjectPart[]> }>(`/api/children/${child.id}/attendance/subjects?from=${data.days[0]!.date}&to=${data.days[6]!.date}`)
      .then((r) => setSplits(r.days))
      .catch(() => setSplits({}));
  }, [child.id, data]);

  async function save(date: string, raw: string) {
    const text = raw.trim();
    const hours = text === '' ? null : Number(text);
    if (hours !== null && (Number.isNaN(hours) || hours < 0 || hours > 24)) {
      toast({ text: 'Enter hours between 0 and 24.', tone: 'alert' });
      return;
    }
    try {
      await api.put(`/api/children/${child.id}/attendance/${date}`, { hours });
      load();
      void refresh(); // the state requirement card (#599) counts recorded hours
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  // A 0-hour day's reason (#298). For an absence, offer to move the unfinished assignments forward a school day.
  async function setReason(date: string, reason: string) {
    try {
      await api.put(`/api/children/${child.id}/attendance/${date}`, { hours: 0, reason });
      load();
      if ((reason === 'excused' || reason === 'unexcused') && confirm(`Move ${child.name}’s unfinished assignments from ${short(date)} on forward one school day?`)) {
        const r = await api.post<{ shifted: number }>(`/api/children/${child.id}/planner/shift`, { from: date });
        toast({ text: r.shifted ? `Moved ${r.shifted} assignment${r.shifted === 1 ? '' : 's'} forward a school day.` : 'No unfinished assignments to move.', tone: 'good' });
      }
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  if (!data) return <p className="muted">Loading…</p>;
  const thisWeek = data.days.some((d) => d.date === data.today);
  const end = data.days[6]!.date;

  return (
    <>
    {child.stateCompliance?.on && child.compliance && (
      <p className="muted small">
        Progress toward {child.compliance.requirement.stateName}’s requirement is in State compliance.{' '}
        <Link to={`/kids/${child.id}/compliance`}>See progress</Link>
      </p>
    )}
    <section className="card stack">
      <SchoolBanner
        child={child}
        info={school}
        part="attendance"
        onUpdated={(i) => {
          setSchool(i);
          load();
        }}
      />
      <ConnexusAttendanceCard child={child} onSent={load} />
      <div className="row-between">
        <h2 className="with-icon">
          <CalendarCheck size={18} /> Attendance
        </h2>
        {!thisWeek && (
          <button className="btn small" onClick={() => setWeek(null)}>
            This week
          </button>
        )}
      </div>
      <p className="muted small">
        Record the hours {child.name} actually did school each day. The <strong>system estimate</strong> underneath is ClassGuardian’s
        guess from active time on a monitored computer — handy as a check, but not official attendance.
      </p>

      <div className="cal-head">
        <button className="icon-btn" onClick={() => setWeek(shiftWeek(data.weekStart, -1))} aria-label="Previous week">
          <ChevronLeft size={18} />
        </button>
        <strong>
          {short(data.weekStart)} – {short(end)}
        </strong>
        <button className="icon-btn" onClick={() => setWeek(shiftWeek(data.weekStart, 1))} aria-label="Next week">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="report-table-wrap">
        <table className="report-table attendance-table">
          <thead>
            <tr>
              <th>Day</th>
              <th>Actual hours</th>
              <th>By subject</th>
              <th>System estimate</th>
              <th className="att-activity">Activity</th>
            </tr>
          </thead>
          <tbody>
            {data.days.map((d) => (
              <Fragment key={d.date}>
              <tr className={`${d.schoolDay ? '' : 'no-school'}${d.date === data.today ? ' today' : ''}`}>
                <td>
                  <strong>{d.weekday}</strong> <span className="muted small">{short(d.date)}</span>
                  {!d.schoolDay && <span className="muted small block">No school</span>}
                  {!!d.fieldTrips?.length && <span className="trip-tag">Field trip: {d.fieldTrips.join(', ')}</span>}
                </td>
                <td>
                  <input
                    key={`${d.date}:${d.actualHours ?? ''}`}
                    className="att-hours"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={24}
                    step={0.25}
                    defaultValue={d.actualHours ?? ''}
                    placeholder="—"
                    disabled={d.locked}
                    title={d.locked ? 'This school year is closed. Reopen it in Settings → School Settings to change it.' : undefined}
                    aria-label={`Actual hours on ${d.weekday} ${d.date}`}
                    onBlur={(e) => {
                      if (e.target.value !== String(d.actualHours ?? '')) save(d.date, e.target.value);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                  />
                  {d.locked && <Lock size={13} className="att-lock" aria-label="Closed school year" />}
                  {d.source === 'connexus' && (
                    <span className="tag school-tag att-source" title="A school day: lessons were finished in Connexus. Change it here and it’s yours instead.">
                      From Connexus lessons
                    </span>
                  )}
                  {d.source === 'connexus-attendance' && (
                    <span className="tag school-tag att-source" title="Connexus’s own attendance, sent with the bookmark. Change it here and it’s yours instead.">
                      Connexus attendance
                    </span>
                  )}
                  {d.actualHours === 0 && !d.locked && (
                    <select className="att-reason" value={d.reason ?? ''} onChange={(e) => setReason(d.date, e.target.value)} aria-label={`Why no school on ${d.weekday} ${d.date}`}>
                      <option value="">Why? (optional)</option>
                      {REASONS.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {d.locked ? (
                    <span className="muted small">{(splits[d.date] ?? []).map((p) => `${p.subject} ${p.hours}`).join(' · ') || '—'}</span>
                  ) : (
                    <SplitCell parts={splits[d.date] ?? []} hours={d.actualHours ?? null} open={splitting === d.date} onToggle={() => setSplitting(splitting === d.date ? '' : d.date)} />
                  )}
                </td>
                <td className="muted" title={d.estimateStored ? 'Saved at the end of the day' : undefined}>
                  {d.estimatedHours ? hrs(d.estimatedHours) : '—'}
                </td>
                <td className="muted small att-activity">{d.firstSeen && d.lastSeen ? `${clock(d.firstSeen)}–${clock(d.lastSeen)}` : ''}</td>
              </tr>
              {splitting === d.date && !!d.actualHours && (
                <tr className="split-editor-row">
                  <td colSpan={5}>
                    <SplitEditor
                      child={child}
                      date={d.date}
                      dayLabel={d.weekday}
                      hours={d.actualHours}
                      parts={splits[d.date] ?? []}
                      onSaved={(parts) => {
                        setSplits((cur) => ({ ...cur, [d.date]: parts }));
                        setSplitting('');
                        setSplitVersion((v) => v + 1);
                        void refresh();
                      }}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Week</th>
              <th>{hrs(data.totals.actualHours)}</th>
              <th />
              <th className="muted">{hrs(data.totals.estimatedHours)}</th>
              <th className="att-activity" />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
    <TimeBySubject
      child={child}
      from={child.schoolYear?.start ?? new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10)}
      to={child.schoolYear?.end ?? new Date().toISOString().slice(0, 10)}
      version={splitVersion}
    />
    </>
  );
}
